import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setBuyer, setAuthenticated } from "../../../redux/slices/authSlice";
import BackButton from "../../../components/BackButton";
import InputField from "../../../components/InputField";
import AuthCard from "../../../components/AuthCard";
import Button from "../../../components/Button";
import FormGroup from "../../../components/FormGroup";
import RequiredLabel from "../../../components/RequiredLabel";
import { loginWithEmail } from "./services/authServices";
import { showNotification } from "../../../redux/slices/notificationSlice";
import ErrorText from "../../../components/ErrorText";

const SignInEmail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setLocalEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormValid = email.trim() !== "" && password.trim() !== "";
  const requiredReason = t("pleaseCompleteRequiredFields", {
    defaultValue: "Please complete all required fields correctly.",
  });

  const handleSignIn = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setError("");
    try {
      const { token, buyer } = await loginWithEmail(email, password);
      localStorage.setItem("jwtToken", token);
      localStorage.setItem("buyer", JSON.stringify(buyer));
      dispatch(setBuyer(buyer));
      dispatch(setAuthenticated(true));
      dispatch(showNotification({ message: t('signedIn'), type: "success" }));
      navigate("/dashboard");
    } catch (err) {
      setError(t('invalidEmailOrPassword'));
      dispatch(
        showNotification({
          message: t('invalidEmailOrPassword'),
          type: "error",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50 animate-fade-in">
      <AuthCard>
        <BackButton />
        <h2 className="main-header mb-6 text-center">{t("signIn")}</h2>
        <form
          className="flex flex-col mb-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSignIn();
          }}
        >
          <FormGroup>
            <RequiredLabel htmlFor="signin-email" className="mb-2">
              {t("emailAddress")}
            </RequiredLabel>

            <InputField
              id="signin-email"
              type="email"
              placeholder={t("enterEmail")}
              value={email}
              onChange={(e) => setLocalEmail(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <RequiredLabel htmlFor="signin-password" className="mb-2">
              {t("password")}
            </RequiredLabel>

            <InputField
              id="signin-password"
              type="password"
              placeholder={t("enterPassword")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormGroup>
          <ErrorText>{error}</ErrorText>
          <Button
            className="w-full"
            disabled={!isFormValid || isSubmitting}
            disabledReason={!isFormValid ? requiredReason : ""}
            loading={isSubmitting}
            loadingLabel={t("loading")}
            type="submit"
          >
            {t("signIn")}
          </Button>

          <div className="text-right">
            <button
              type="button"
              className="text-sm underline bg-transparent border-none cursor-pointer p-0"
              onClick={() => navigate("/auth/forgot-password")}
            >
              {t("forgotPassword")}
            </button>
          </div>
        </form>
        <hr />

        <div className="mt-4 text-sm text-gray-500 text-center">
          {t("dontHaveAccount")}
        </div>

        <div className="flex gap-3 justify-center mt-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/auth/sign-up-with-email")}
          >
            {t("signUp")}
          </Button>
        </div>
      </AuthCard>
    </div>
  );
};

export default SignInEmail;
